// src/services/authService.js
import rsaEncryption from '../utils/rsaEncryption';
import apiClient from './apiClient';

const AUTH_ENDPOINT = '/auth';

export const authService = {
  // Store tokens and user info
  setTokens(accessToken, refreshToken) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  },

  // Clear tokens and user info
  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('accessToken');
  },
// Get public key from server
  async getPublicKey() {
    try {
      const response = await apiClient.get(`${AUTH_ENDPOINT}/public-key`);
      const { publicKey } = response.data;
      
      // Import the public key for encryption
      await rsaEncryption.importPublicKey(publicKey);
      
      return publicKey;
    } catch (error) {
      console.error('Failed to get public key:', error);
      throw new Error('Failed to get encryption key from server');
    }
  },

  // Prepare encrypted request data
  async prepareEncryptedData(data) {
    try {
      // Ensure we have the public key
      if (!rsaEncryption.isReady()) {
        await this.getPublicKey();
      }

      // Encrypt the data
      const encryptedData = await rsaEncryption.encrypt(data);
      
      return { encryptedData };
    } catch (error) {
      console.error('Failed to encrypt data:', error);
      throw new Error('Failed to encrypt request data');
    }
  },
  // Login
  async login(username, password) {
    try {
       const encryptedPayload = await this.prepareEncryptedData({
        username,
        password
      });

      const response = await apiClient.post(`${AUTH_ENDPOINT}/login`, encryptedPayload);
      
      // Store tokens and user info
      const { accessToken, refreshToken, user } = response.data;
      this.setTokens(accessToken, refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      return response.data;
    } catch (error) {
      // Handle specific error responses
      if (error.response) {
        throw new Error(error.response.data.message || 'Login failed');
      }
      throw new Error('Network error. Please try again later.');
    }
  },

  // Logout
  async logout() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      // Only make the API call if we have a refresh token
      if (refreshToken) {
        await apiClient.post(`${AUTH_ENDPOINT}/logout`, { refreshToken });
      }
      
      // Clear local storage regardless of API response
      this.clearTokens();
      return { success: true };
    } catch (error) {
      // Clear tokens even if the API call fails
      this.clearTokens();
      
      // Throw error for UI handling if needed
      if (error.response) {
        throw new Error(error.response.data.message || 'Logout failed');
      }
      throw new Error('Network error during logout');
    }
  },

  // Change password
  async changePassword(userId,currentPassword, newPassword, changeDateTime) {
    try {

       // Prepare encrypted password data
      const encryptedPayload = await this.prepareEncryptedData({
        userId,
        currentPassword,
        newPassword,
        confirmNewPassword: newPassword,
        changeDateTime
      });
      
      const response = await apiClient.post(`${AUTH_ENDPOINT}/change-password`,  encryptedPayload);
      
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to change password');
      }
      throw new Error('Network error. Please try again later.');
    }
  },
 
// Forgot password
  async forgotPassword(email) {
    try {
      // Prepare encrypted email data
      const encryptedPayload = await this.prepareEncryptedData({ email });

      const response = await apiClient.post(`${AUTH_ENDPOINT}/forgot-password`, encryptedPayload);
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to process request');
      }
      throw new Error('Network error. Please try again later.');
    }
  },

  // Reset password
  async resetPassword(token, newPassword) {
    try {
      // Prepare encrypted password data
      const encryptedPayload = await this.prepareEncryptedData({
        token,
        newPassword
      });

      const response = await apiClient.post(`${AUTH_ENDPOINT}/reset-password`, encryptedPayload);
      
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to reset password');
      }
      throw new Error('Network error. Please try again later.');
    }
  },
 /*
  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await apiClient.post(`${AUTH_ENDPOINT}/forgot-password`, { email });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to process request');
      }
      throw new Error('Network error. Please try again later.');
    }
  },

  // Reset password
  async resetPassword(token, email, newPassword) {
    try {
      const response = await apiClient.post(`${AUTH_ENDPOINT}/reset-password`, {
        token,
        newPassword
      });
      
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to reset password');
      }
      throw new Error('Network error. Please try again later.');
    }
  },*/
  
  // Refresh token
  async refreshToken(refreshToken) {
    try {
      const response = await apiClient.post(`${AUTH_ENDPOINT}/refresh-token`, { 
        refreshToken 
      });
      
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      this.setTokens(accessToken, newRefreshToken);
      
      return response.data;
    } catch (error) {
      // Clear tokens if refresh fails
      this.clearTokens();
      throw new Error('Session expired. Please log in again.');
    }
  },

 // Get current user
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Check if token is valid/refresh if needed
  async checkAuth() {
    // This would typically involve validating the token with the server
    // or refreshing it if needed
    // For now, we'll just check if the token exists
    return this.isAuthenticated();
  },

  // Initialize encryption (call this on app startup)
  async initializeEncryption() {
    try {
      await this.getPublicKey();
      return true;
    } catch (error) {
      console.warn('Failed to initialize encryption:', error);
      return false;
    }
  },
  async checkEmail(email) {
  try {
    // Prepare encrypted email data
    //const encryptedPayload = await this.prepareEncryptedData({ email });

    const response = await apiClient.post(`${AUTH_ENDPOINT}/check-email`, { email });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to check email');
    }
    throw new Error('Network error. Please try again later.');
  }
}
};

export default authService;